/**
 * One calendar day, asked for once, for every screen that asks for one.
 *
 * ── Why the box itself still prints `mm/dd/yyyy` ─────────────────────────────
 *
 * `<input type="date">` renders its own text, and it renders it in the **browser's**
 * locale, not the document's. `web/index.html` carries `lang="vi"` and
 * `i18n/index.tsx` keeps `document.documentElement.lang` in step with the chosen
 * locale on every change — both are correct and neither changes what Chrome prints
 * inside a date control, which is why the review capture, taken by an `en-US`
 * headless browser, reads `09/14/2026` on a page that is Vietnamese everywhere else.
 *
 * There is no attribute that fixes it, and the alternative — a hand-built calendar
 * popover — trades the platform's keyboard handling, its mobile wheel, its screen
 * reader support and its locale awareness for a control this product would then own
 * forever. So the native control stays, and the **product's own voice says the date
 * underneath it**: one 12px line, through `formatDay`, the same formatter
 * `/overview` and every other screen prints a date through (spec 01 §19, "one date
 * voice"). Whatever the browser chrome says, the sentence a person reads is
 * "14 tháng 9, 2026". An empty control gets the same treatment: the browser prints its
 * own placeholder (`mm/dd/yyyy`, `dd/mm/yyyy`, …), so the line underneath says
 * "Chưa chọn ngày" — the one cue in the product's voice that the box is still empty.
 *
 * Recorded in `DESIGN.md` §14.
 */

import type { ReactNode } from 'react';

import { useI18n } from '../i18n';
import { formatDay } from '../i18n/datetime';

/** What `<input type="date">` hands back once a day is chosen. Anything else is mid-edit. */
const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;

export interface DateFieldProps {
  /** DOM id of the control; the label points at it and the helper line is described by it. */
  readonly id: string;
  readonly label: string;
  /** `YYYY-MM-DD`, or `''` for "no day chosen". */
  readonly value: string;
  readonly onChange: (next: string) => void;
  /** Passed straight through — `/attendance` bars a future day with it. */
  readonly max?: string;
  /** `data-testid` of the **input**, so a test still types into the control itself. */
  readonly testId?: string;
  /** The wrapper's class. `.wp-field` unless the caller's bar wants `.wp-filters__group`. */
  readonly className?: string;
  /**
   * Copy the caller keeps **inside** the field, under the helper line — `/payroll`'s
   * composer explains what "áp dụng từ" means there. Outside the wrapper it would fall
   * past `.wp-field`'s bottom margin and read as a stray sentence.
   */
  readonly children?: ReactNode;
}

export function DateField({
  id,
  label,
  value,
  onChange,
  max,
  testId,
  className = 'wp-field',
  children,
}: DateFieldProps): ReactNode {
  const { t, locale } = useI18n();
  const helperId = `${id}-chosen`;
  const readable = ISO_DAY.test(value)
    ? formatDay(value, locale, { day: 'numeric', month: 'long', year: 'numeric' })
    : null;

  return (
    <div className={className}>
      <label className="wp-field__label" htmlFor={id}>
        {label}
      </label>
      <input
        id={id}
        className="wp-field__input"
        type="date"
        value={value}
        {...(max === undefined ? {} : { max })}
        aria-describedby={helperId}
        {...(testId === undefined ? {} : { 'data-testid': testId })}
        onChange={(event) => {
          onChange(event.target.value);
        }}
      />
      <p
        className="wp-small wp-muted"
        id={helperId}
        {...(testId === undefined ? {} : { 'data-testid': `${testId}-chosen` })}
      >
        {readable === null ? t('date.none') : t('date.chosen', { date: readable })}
      </p>
      {children}
    </div>
  );
}
